import {Directive, inject, Input, OnDestroy, TemplateRef, ViewContainerRef} from '@angular/core';
import {select, Store} from '@ngrx/store';
import {filter, Observable, Subscription} from 'rxjs';

import {Role, RolePermissionOptions, RolePermissions} from '../../core/model/role';
import {selectActiveUserRole} from '../../core/store/active-user/active-user.selector';
import {getRolePermissionByKey} from './utils/get-role-permission-by-key';

@Directive({
  selector: '[userHasRoleOption]',
  standalone: true,
})
export class UserHasRoleOptionDirective implements OnDestroy {
  private store$ = inject(Store);
  private viewContainer = inject(ViewContainerRef);
  private templateRef = inject(TemplateRef<unknown>);
  private elseTemplateRef: TemplateRef<unknown> | null = null;
  private currentOptionKey: RolePermissionOptions | null = null;
  private lastIsActive: boolean | null = null;

  userRole$: Observable<Role> = this.store$.pipe(select(selectActiveUserRole));
  private subscription: Subscription = new Subscription();

  @Input() set userHasRoleOption(optionKey: RolePermissionOptions) {
    this.currentOptionKey = optionKey;
    this.updateView();
  }

  @Input() set userHasRoleOptionElse(templateRef: TemplateRef<unknown>) {
    this.elseTemplateRef = templateRef;
    this.updateElseView();
  }

  private updateView() {
    if (!this.currentOptionKey) return;

    this.subscription.unsubscribe();
    this.subscription = this.userRole$.pipe(filter(userRole => !!userRole)).subscribe(userRole => {
      if (!this.currentOptionKey) return;

      const userRolePermissions: RolePermissions = userRole.permissions;
      const permissionObj = getRolePermissionByKey(userRolePermissions, this.currentOptionKey);
      const isActive = permissionObj?.active === true;

      /**
       * Only recreate the view if the permission state changes. Constant clearing and recreating
       * causes Angular to lose the state and context of any dynamic content inside the element
       * that has the directive
       */
      if (isActive === this.lastIsActive) return;
      this.lastIsActive = isActive;

      this.viewContainer.clear();

      if (isActive) {
        this.viewContainer.createEmbeddedView(this.templateRef);
      }
    });
  }

  private updateElseView(): void {
    if (!this.elseTemplateRef) return;

    this.viewContainer.clear();
    this.viewContainer.createEmbeddedView(this.elseTemplateRef);
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }
}
